import { usePageLocale } from "@/components/seo/usePageLocale";
import { normalizeSeoLanguage } from "@/lib/seoMetadata";
import { SITE_ORIGIN } from "@/lib/urlNormalise";

// One declaration of the origin, in `urlNormalise.ts`; this is a name for it.
// SYSTEM_DESIGN.md 11.3, CWS-67.
const BASE_URL = SITE_ORIGIN;
const ORG_NAME = "Shelpuk AI Technology Consulting";
const REPORT_LANGUAGE_LABELS = {
  en: "en",
  de: "de",
  fr: "fr",
  it: "it",
  es: "es",
} as const;
const ORG_REF = {
  "@type": "Organization",
  name: ORG_NAME,
  url: BASE_URL,
  logo: `${BASE_URL}/logo.png`,
};

/**
 * Build the `Service` block for one service page.
 *
 * **`Service`, not `ProfessionalService`** (CWS-45, `SEO.md` `B18`).
 * schema.org deprecated the latter "due to confusion with `Service`", and it is
 * a `LocalBusiness` subtype under both `Organization` and `Place` — so it
 * asserted business premises this block never describes. No rich result was
 * lost: a `LocalBusiness` result needs an `address`, which these blocks have
 * never carried. Every property emitted here exists on `Service`.
 *
 * **No `inLanguage`.** schema.org lists the property on seven types —
 * `CreativeWork` among them, bare `Service` not — so the stamp this block used
 * to carry was out of domain. The page's language reaches a crawler through
 * `<html lang>`, the canonical, `hreflang` and `og:locale`. See
 * `SYSTEM_DESIGN.md` §13a.2 for the house rule that replaced the inherited
 * split.
 *
 * @param name - The service name, as shown on the page.
 * @param description - The service description, already localized.
 * @param path - The locale-less route path, with a leading slash, for example
 *     ``/services/consulting``.
 * @param lang - The page locale; normalised through
 *     :func:`normalizeSeoLanguage`.
 * @returns The `Service` object for the page.
 */
export function buildServicePageJsonLdData({
  name,
  description,
  path,
  lang,
}: {
  name: string;
  description: string;
  path: string;
  lang: string;
}) {
  const safeLang = normalizeSeoLanguage(lang);

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${name} — ${ORG_NAME}`,
    description,
    provider: ORG_REF,
    areaServed: ["EU", "Europe"],
    url: `${BASE_URL}/${safeLang}${path}`,
    serviceType: name,
  };
}

/**
 * `Service` schema for individual service pages.
 * Tells AI crawlers exactly what service is offered, by whom, and for which market.
 */
export function ServicePageJsonLd({
  name,
  description,
  path,
}: {
  name: string;
  description: string;
  path: string;
}) {
  const lang = usePageLocale();
  const data = buildServicePageJsonLdData({ name, description, path, lang });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

/**
 * Build the `Service` block for one industry landing page.
 *
 * `name` and `serviceType` are the `industry` argument, which every call site
 * passes from ``t("<vertical>Page.badge")`` — the same localized string the
 * page renders as its visible badge, which is what makes this block satisfy
 * invariant `I14`.
 *
 * **The `AI Consulting for` prefix is gone** (CWS-45, `SEO.md` `B18`). It was
 * English while `industry` arrived localized, so four industry pages × four
 * non-English locales emitted a mixed-language name such as
 * ``AI Consulting for IA Pharma & Santé — …`` — a string no page anywhere on
 * the site rendered. Localizing the prefix was rejected: it needs five new
 * strings, still names something the page does not say, and the badges already
 * carry AI / IA / KI, so the prefix was largely redundant.
 *
 * See :func:`buildServicePageJsonLdData` for why the `@type` is `Service` and
 * why no `inLanguage` is emitted.
 *
 * @param industry - The industry vertical, already localized, for example
 *     ``Pharma & Healthcare AI``.
 * @param description - The page description, already localized.
 * @param path - The locale-less route path, with a leading slash, for example
 *     ``/industries/pharma``.
 * @param lang - The page locale; normalised through
 *     :func:`normalizeSeoLanguage`.
 * @returns The `Service` object for the page.
 */
export function buildIndustryPageJsonLdData({
  industry,
  description,
  path,
  lang,
}: {
  industry: string;
  description: string;
  path: string;
  lang: string;
}) {
  const safeLang = normalizeSeoLanguage(lang);

  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${industry} — ${ORG_NAME}`,
    description,
    provider: ORG_REF,
    areaServed: ["EU", "Europe"],
    url: `${BASE_URL}/${safeLang}${path}`,
    serviceType: industry,
    knowsAbout: [
      industry,
      "Artificial Intelligence",
      "Machine Learning",
      "AI Strategy",
    ],
  };
}

/**
 * `Service` schema specialised for industry verticals.
 * Helps AI bots associate the company with specific industries.
 */
export function IndustryPageJsonLd({
  industry,
  description,
  path,
}: {
  industry: string;
  description: string;
  path: string;
}) {
  const lang = usePageLocale();
  const data = buildIndustryPageJsonLdData({ industry, description, path, lang });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

/**
 * Build the `SoftwareApplication` block for one open-source tool page.
 *
 * @param name - The tool name.
 * @param description - The tool description, already localized.
 * @param path - The locale-less route path, with a leading slash, for example
 *     ``/open-source/tdd``.
 * @param repoUrl - The public source repository URL.
 * @param lang - The page locale; normalised through
 *     :func:`normalizeSeoLanguage`.
 * @returns The `SoftwareApplication` object for the page.
 */
export function buildSoftwareJsonLdData({
  name,
  description,
  path,
  repoUrl,
  lang,
}: {
  name: string;
  description: string;
  path: string;
  repoUrl: string;
  lang: string;
}) {
  const safeLang = normalizeSeoLanguage(lang);

  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    description,
    url: `${BASE_URL}/${safeLang}${path}`,
    codeRepository: repoUrl,
    applicationCategory: "DeveloperApplication",
    operatingSystem: "Cross-platform",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    author: ORG_REF,
    inLanguage: safeLang,
  };
}

/**
 * SoftwareApplication schema for open-source tool pages.
 * Makes AI chatbots aware of freely available developer tools built by the company.
 */
export function SoftwareJsonLd({
  name,
  description,
  path,
  repoUrl,
}: {
  name: string;
  description: string;
  path: string;
  repoUrl: string;
}) {
  const lang = usePageLocale();
  const data = buildSoftwareJsonLdData({ name, description, path, repoUrl, lang });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

/**
 * Report / CreativeWork schema for the Insights page.
 * Surfaces the company's published research to AI models.
 */
export function ReportJsonLd({
  title,
  description,
  path,
}: {
  title: string;
  description: string;
  path: string;
}) {
  const lang = usePageLocale();
  const data = buildReportJsonLdData({ title, description, path, lang });

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

export function buildReportJsonLdData({
  title,
  description,
  path,
  lang,
}: {
  title: string;
  description: string;
  path: string;
  lang: string;
}) {
  const safeLang = normalizeSeoLanguage(lang);

  return {
    "@context": "https://schema.org",
    "@type": "Report",
    name: title,
    description,
    url: `${BASE_URL}/${safeLang}${path}`,
    author: ORG_REF,
    publisher: ORG_REF,
    datePublished: "2026-01",
    inLanguage: REPORT_LANGUAGE_LABELS[safeLang],
  };
}
